import React, { useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { useLocation } from 'react-router-dom';
import {
  Transaction,
  TRANSACTION_STATUS,
  TRANSACTION_TYPES_NAME_MAP,
} from '../../data/types/transactions';
import { formatPrice } from '../../utils/functions/formatting_functions';
import {
  TransactionStatusTag,
  TransactionsFilterBtns,
} from './components/shared_components';
import momentZ from 'moment-timezone';
import { styled } from 'styled-components';
import { Colors } from '../../config/theme';
import {
  getTransactioTypeAmountColor,
  getTransactioTypeCashBalanceColor,
} from './functions/ledger_functions';
import { OrderPaymentMethodTag } from '../../pages/orders_archive/components/shared_components';
import { formateServerDateToLocaleDate } from '../../utils/functions/time_date_functions';

export const LedgerPage = ({
  TopNav,
  isSellerTransactions,
  transactions,
}: {
  TopNav?: JSX.Element;
  isSellerTransactions: boolean;
  transactions: Transaction[];
}) => {
  const [filters, setFilters] = useState<{
    types: number[];
    statuses: number[];
    methods: number[];
    date: string[];
  }>({
    types: [],
    date: [],
    statuses: [],
    methods: [],
  });

  const location = useLocation();
  useEffect(() => {
    setFilters(
      location.state?.filters || {
        types: [],
        statuses: [],
        methods: [],
        date: [],
      },
    );
  }, [location]);

  const filteredTransactions = useMemo(() => {
    let results = [...transactions].reverse();
    if (filters.statuses?.length > 0) {
      results = results.filter((item) =>
        filters.statuses.includes(item.transaction_status),
      );
    }
    if (filters.methods?.length > 0) {
      results = results.filter((item) =>
        filters.methods.includes(item.transaction_method),
      );
    }
    if (filters.types?.length > 0) {
      results = results.filter((item) =>
        filters.types.includes(item.transaction_type),
      );
    }
    if (filters.date?.length === 2 && filters?.date[0] !== '') {
      results = results.filter((item) => {
        const date = momentZ(item.transaction_creation_date);
        const startDate = momentZ(filters?.date[0]);
        const endDate = momentZ(filters?.date[1]);
        return date.isBetween(startDate, endDate, 'day', '[]');
      });
    }
    return results;
  }, [filters, transactions]);

  const commonColumns = [
    {
      title: 'ID',
      dataIndex: 'transaction_id',
      render: (text: string) => `#${text}`,
    },
    {
      title: 'Created',
      dataIndex: 'transaction_creation_date',
      render: (text: string) => formateServerDateToLocaleDate(text),
    },
    {
      title: 'Completed',
      dataIndex: 'transaction_completion_date',
      render: (text: string) => formateServerDateToLocaleDate(text),
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      render: (text: number) => TRANSACTION_TYPES_NAME_MAP[text],
    },
    {
      title: 'Amount',
      dataIndex: 'transaction_amount',
      render: (text: string, record: any) => (
        <span className={getTransactioTypeAmountColor(record.transaction_type)}>
          {formatPrice(text)}
        </span>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'transaction_method',
      render: (text: number) => OrderPaymentMethodTag(text),
    },
    {
      title: 'Status',
      dataIndex: 'transaction_status_name',
      render: (text: string) => TransactionStatusTag(text),
    },
    {
      title: 'Additional Context',
      dataIndex: 'additional_context',
      render: (text: any) => <p>{JSON.stringify(text)}</p>,
    },
  ];

  const sellerColumns = [
    ...commonColumns,
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      render: (text: string) => (text === undefined ? 'N/A' : `#${text}`),
    },
    {
      title: 'Debt to Seller',
      dataIndex: 'balance',
      render: (text: string, record: any) => (
        <span
          className={
            record.transaction_status === TRANSACTION_STATUS.pending
              ? 'pending-amount'
              : record.amount > 0
                ? 'negative-amount'
                : 'positive-amount'
          }
        >
          {formatPrice(text)}
        </span>
      ),
    },
  ];

  const driverColumns = [
    ...commonColumns,
    {
      title: 'Carried Cash',
      dataIndex: 'cash_balance_debt_to_silal',
      render: (text: string, record: any) => (
        <span
          className={
            record.transaction_status === TRANSACTION_STATUS.pending
              ? 'pending-amount'
              : getTransactioTypeCashBalanceColor(record.transaction_type)
          }
        >
          {formatPrice(text)}
        </span>
      ),
    },
    {
      title: 'Debt to Captain',
      dataIndex: 'revenue_balance_debt_to_driver',
      render: (text: string, record: any) => (
        <span
          className={
            record.transaction_status === TRANSACTION_STATUS.pending
              ? 'pending-amount'
              : record.amount > 0
                ? 'negative-amount'
                : 'positive-amount'
          }
        >
          {formatPrice(text)}
        </span>
      ),
    },
  ];

  const columns = isSellerTransactions ? sellerColumns : driverColumns;

  return (
    <Wrapper>
      {TopNav}
      <div className="filters">
        <TransactionsFilterBtns
          transactions={transactions}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      <Table
        rowKey="transaction_id"
        columns={columns}
        dataSource={filteredTransactions}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .positive-amount {
    color: ${Colors.greenMain};
  }
  .negative-amount {
    color: ${Colors.tomatoRed};
  }
  .pending-amount {
    color: ${Colors.grey};
  }
`;
